/**
 * Períodos da página: mês, quinzena (1–15 e 16–fim) e semana (segunda a
 * domingo). O dia não tem ocupação (decisão 6): só lista, escolhida na tela.
 */
import { fimDoMes, inicioDaSemana, inicioDoMes, somarDiasCorridos, somarMeses } from "./calendario";

export const ESCALA = Object.freeze({ MES: "mes", QUINZENA: "quinzena", SEMANA: "semana", DIA: "dia" });

export const RELACAO = Object.freeze({ PASSADO: "passado", CORRENTE: "corrente", FUTURO: "futuro" });

/** Quantos períodos a página mostra (seção 2.4). */
export const QUANTIDADE_PADRAO = Object.freeze({
  [ESCALA.MES]: { seguintes: 5, anteriores: 6 },
  [ESCALA.QUINZENA]: { seguintes: 6, anteriores: 6 },
  [ESCALA.SEMANA]: { seguintes: 8, anteriores: 8 },
});

const MESES_CURTOS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function rotuloMes(iso) {
  return `${MESES_CURTOS[Number(iso.slice(5, 7)) - 1]}/${iso.slice(0, 4)}`;
}

function ddmm(iso) {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

function relacaoCom(hoje, inicio, fim) {
  if (fim < hoje) return RELACAO.PASSADO;
  if (inicio > hoje) return RELACAO.FUTURO;
  return RELACAO.CORRENTE;
}

function periodo(escala, inicio, fim, rotulo, hoje) {
  return { chave: `${escala}:${inicio}`, escala, inicio, fim, rotulo, relacao: relacaoCom(hoje, inicio, fim) };
}

function mesDeslocado(hoje, deslocamento) {
  const inicio = somarMeses(hoje, deslocamento);
  return periodo(ESCALA.MES, inicio, fimDoMes(inicio), rotuloMes(inicio), hoje);
}

function quinzenaDe(iso, hoje) {
  const mes = inicioDoMes(iso);
  const primeira = Number(iso.slice(8, 10)) <= 15;
  const inicio = primeira ? mes : `${iso.slice(0, 7)}-16`;
  const fim = primeira ? `${iso.slice(0, 7)}-15` : fimDoMes(iso);
  return periodo(ESCALA.QUINZENA, inicio, fim, `${primeira ? "1ª" : "2ª"} quinzena ${rotuloMes(iso)}`, hoje);
}

function semanaDeslocada(hoje, deslocamento) {
  const inicio = somarDiasCorridos(inicioDaSemana(hoje), 7 * deslocamento);
  const fim = somarDiasCorridos(inicio, 6);
  return periodo(ESCALA.SEMANA, inicio, fim, `${ddmm(inicio)} a ${ddmm(fim)}`, hoje);
}

function quinzenaDeslocada(hoje, deslocamento) {
  let atual = quinzenaDe(hoje, hoje);
  const passo = deslocamento >= 0 ? 1 : -1;
  for (let i = 0; i < Math.abs(deslocamento); i += 1) {
    const vizinho = passo > 0 ? somarDiasCorridos(atual.fim, 1) : somarDiasCorridos(atual.inicio, -1);
    atual = quinzenaDe(vizinho, hoje);
  }
  return atual;
}

const GERADORES = {
  [ESCALA.MES]: mesDeslocado,
  [ESCALA.QUINZENA]: quinzenaDeslocada,
  [ESCALA.SEMANA]: semanaDeslocada,
};

/**
 * Períodos da escala em ordem cronológica: `anteriores` passados, o
 * corrente e `seguintes` futuros. Cada um: `{chave, escala, inicio, fim,
 * rotulo, relacao}`.
 */
export function listarPeriodos(escala, hoje, quantidade = QUANTIDADE_PADRAO[escala]) {
  const gerar = GERADORES[escala];
  if (!gerar) throw new TypeError(`Escala sem períodos: ${String(escala)}.`);
  const { seguintes = 0, anteriores = 0 } = quantidade || {};
  const lista = [];
  for (let d = -anteriores; d <= seguintes; d += 1) lista.push(gerar(hoje, d));
  return lista;
}
